import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import {
  FormControlLabel,
  Hidden,
  Menu,
  MenuItem,
  Switch,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import AudioRecorderService, {
  getAudioRecorderErrorKey,
} from "../../services/AudioRecorderService";

const useStyles = makeStyles(theme => ({
  mainWrapper: {
    background: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      position: "sticky",
      bottom: 0,
      width: "100%",
      zIndex: 2,
      paddingBottom: "env(safe-area-inset-bottom)",
    },
  },

  newMessageBox: {
    background: theme.palette.background.paper,
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : theme.palette.background.default,
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
    color: theme.palette.text.primary,
    "& input, & textarea": {
      color: theme.palette.text.primary,
    },
  },

  internalMessageInputWrapper: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 193, 7, 0.16)"
        : "rgba(255, 193, 7, 0.22)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 193, 7, 0.45)"
        : "rgba(160, 112, 0, 0.28)"
    }`,
  },

  internalModeHint: {
    alignSelf: "stretch",
    color: theme.palette.text.secondary,
    fontSize: 12,
    padding: theme.spacing(0.5, 8, 0),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.5, 2, 0),
    },
  },

  internalModeIconActive: {
    color: theme.palette.warning?.main || "#f9a825",
  },

  sendMessageIcons: {
    color: theme.palette.text.secondary,
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  modalActionButton: {
    borderRadius: 999,
    boxShadow: "none",
    textTransform: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    padding: "2px",
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": {
          background:
            theme.palette.type === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.04)",
          cursor: "pointer",
        },
      },
    },
  },

  mentionSuggestionsWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.type === "dark" ? "#202c33" : theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxShadow: theme.shadows[6],
    color: theme.palette.text.primary,
    left: 0,
    maxHeight: 260,
    overflowY: "auto",
    padding: theme.spacing(0.75),
    width: "min(420px, 92vw)",
    zIndex: 4,
    ...theme.scrollbarStyles,
  },

  mentionSuggestionItem: {
    alignItems: "center",
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    gap: theme.spacing(1.25),
    listStyle: "none",
    minHeight: 48,
    padding: theme.spacing(0.75, 1),
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.08)"
          : "rgba(0, 0, 0, 0.04)",
    },
  },

  mentionSuggestionItemActive: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(117, 45, 230, 0.08)",
  },

  mentionSuggestionAvatar: {
    height: 36,
    width: 36,
  },

  mentionSuggestionAllAvatar: {
    backgroundColor: theme.palette.primary.main,
  },

  mentionSuggestionText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  mentionSuggestionName: {
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  mentionSuggestionHint: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const OUTSIDE_24H_ERROR_KEYS = new Set([
  "ERR_OFFICIAL_TEMPLATE_REQUIRED_OUTSIDE_24H",
  "ERR_OFFICIAL_TEMPLATE_NOT_ALLOWED_FOR_QUEUE",
  "ERR_OFFICIAL_MESSAGE_BLOCKED_BY_META",
  "ERR_OFFICIAL_TEMPLATE_ALREADY_SENT",
]);

const getErrorKey = err =>
  String(err?.response?.data?.message || err?.response?.data?.error || "").trim();

const parseOfficialConnectionIdFromSession = session => {
  const normalized = String(session || "").trim();
  const match = normalized.match(/^official-(\d+)$/);
  return match ? Number(match[1]) : null;
};

const resolveOfficialConnectionId = async ticket => {
  const whatsappId = Number(ticket?.whatsappId);

  // Always prioritize the current whatsappId from the ticket payload because
  // ticket.whatsapp.session can be stale after realtime updates.
  if (Number.isFinite(whatsappId) && whatsappId > 0) {
    try {
      const { data } = await api.get(`/whatsapp/${whatsappId}`);
      const resolvedFromApi = parseOfficialConnectionIdFromSession(data?.session);

      if (resolvedFromApi) {
        return resolvedFromApi;
      }
    } catch (_err) {
      // Fall back to local ticket data when the lookup endpoint is temporarily unavailable.
    }
  }

  return parseOfficialConnectionIdFromSession(ticket?.whatsapp?.session);
};

const normalizeTemplateLanguageCode = value => {
  let rawLanguage = "";

  if (typeof value === "string") {
    rawLanguage = value;
  } else if (value && typeof value === "object") {
    rawLanguage = String(
      value.code || value.language || value.locale || value.value || ""
    );
  } else {
    rawLanguage = String(value || "");
  }

  rawLanguage = rawLanguage.trim().replace("-", "_");

  if (!rawLanguage) {
    return "pt_BR";
  }

  const match = rawLanguage.match(/^([a-z]{2})_([a-z]{2})$/i);

  if (!match) {
    return rawLanguage;
  }

  return `${match[1].toLowerCase()}_${match[2].toUpperCase()}`;
};

const getTemplateExampleText = component => {
  const examples = component?.example;
  const bodyExample = Array.isArray(examples?.body_text)
    ? examples.body_text.flat().find(item => String(item || "").trim())
    : "";
  const headerExample = Array.isArray(examples?.header_text)
    ? examples.header_text.flat().find(item => String(item || "").trim())
    : "";

  return String(bodyExample || headerExample || "").trim();
};

const getTemplateBodyPreview = template => {
  const components = Array.isArray(template?.components) ? template.components : [];

  const bodyComponent = components.find(
    component => String(component?.type || "").toUpperCase() === "BODY"
  );

  const bodyText = String(bodyComponent?.text || "").trim();
  if (bodyText) {
    return bodyText;
  }

  const bodyExampleText = getTemplateExampleText(bodyComponent);
  if (bodyExampleText) {
    return bodyExampleText;
  }

  const firstTextComponent = components.find(component => {
    const text = String(component?.text || "").trim();
    return Boolean(text);
  });

  if (firstTextComponent) {
    return String(firstTextComponent.text || "").trim();
  }

  const firstComponentWithExample = components.find(component =>
    Boolean(getTemplateExampleText(component))
  );

  if (firstComponentWithExample) {
    return getTemplateExampleText(firstComponentWithExample);
  }

  return "Conteudo de exemplo indisponivel para este template.";
};

const getTemplateVariableInfo = template => {
  const components = Array.isArray(template?.components) ? template.components : [];
  const bodyComponent = components.find(
    component => String(component?.type || "").toUpperCase() === "BODY"
  );
  const bodyText = String(bodyComponent?.text || "");
  const variables = [];
  const pattern = /\{\{\s*([^}]+?)\s*\}\}/g;
  let match;

  while ((match = pattern.exec(bodyText)) !== null) {
    const variable = String(match[1] || "").trim();
    if (variable && !variables.includes(variable)) variables.push(variable);
  }

  const declaredFormat = String(template?.parameter_format || "").toUpperCase();
  const inferredFormat = variables.some(variable => !/^\d+$/.test(variable))
    ? "NAMED"
    : "POSITIONAL";

  return {
    format: declaredFormat === "NAMED" || declaredFormat === "POSITIONAL"
      ? declaredFormat
      : inferredFormat,
    variables: inferredFormat === "POSITIONAL"
      ? variables.sort((left, right) => Number(left) - Number(right))
      : variables,
  };
};

const fillTemplatePreviewWithValues = (rawPreview, parameterValues, contactName) => {
  const contactLabel = String(contactName || "").trim() || "Cliente";
  return String(rawPreview || "").replace(/\{\{\s*([^}]+?)\s*\}\}/g, (match, variable) => {
    const name = String(variable || "").trim();
    const explicitValue = String(parameterValues?.[name] || "").trim();
    if (explicitValue) return explicitValue;
    return /^(name|nome|1)$/i.test(name) ? contactLabel : match;
  });
};

const fetchAllUsers = async () => {
  const users = [];
  let pageNumber = 1;
  let hasMore = true;

  while (hasMore) {
    const { data } = await api.get("/users/", {
      params: { pageNumber },
    });
    const batch = Array.isArray(data?.users) ? data.users : [];
    users.push(...batch);
    hasMore = Boolean(data?.hasMore) && batch.length > 0;
    pageNumber += 1;
  }

  return users;
};

const normalizeMentionText = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const getMentionToken = (value, cursorPosition) => {
  const beforeCursor = String(value || "").slice(0, cursorPosition);
  const match = beforeCursor.match(/(^|\s)@([^\s@]*)$/);

  if (!match) {
    return null;
  }

  return {
    start: beforeCursor.length - match[2].length - 1,
    query: match[2] || "",
  };
};

const getMentionDisplayName = user =>
  String(user?.name || user?.email || "").trim();

const getMentionTokenText = user => {
  const displayName = getMentionDisplayName(user);
  return displayName.replace(/\s+/g, " ").trim();
};

const MessageInput = ({
  ticketStatus,
  ticket,
  onInternalNoteSent = () => {},
}) => {
  const classes = useStyles();
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
  const [templateParameterValues, setTemplateParameterValues] = useState({});
  const [pendingMessage, setPendingMessage] = useState(null);
  const [internalNoteMode, setInternalNoteMode] = useState(false);
  const [mentionableUsers, setMentionableUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(null);
  const [mentionHighlightedIndex, setMentionHighlightedIndex] = useState(0);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  const audioRecorderRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);
  const [allowSignMessageChoice, setAllowSignMessageChoice] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadAllowSignMessageChoice = async () => {
      try {
        const { data } = await api.get("/settings/allowSignMessageChoice");
        if (!mounted) {
          return;
        }

        setAllowSignMessageChoice(String(data?.value || "enabled") !== "disabled");
      } catch (_err) {
        if (mounted) {
          setAllowSignMessageChoice(true);
        }
      }
    };

    loadAllowSignMessageChoice();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      if (audioRecorderRef.current) {
        audioRecorderRef.current.cancel().catch(() => undefined);
      }
      setRecording(false);
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
      setTemplateDialogOpen(false);
      setTemplateOptions([]);
      setSelectedTemplateKey("");
      setTemplateParameterValues({});
      setPendingMessage(null);
      setInternalNoteMode(false);
      setMentionQuery("");
      setMentionStartIndex(null);
      setMentionHighlightedIndex(0);
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => {
    let mounted = true;

    const loadUsers = async () => {
      try {
        const users = await fetchAllUsers();
        if (mounted) {
          setMentionableUsers(users);
        }
      } catch (_err) {
        if (mounted) {
          setMentionableUsers([]);
        }
      }
    };

    if (internalNoteMode && mentionableUsers.length === 0) {
      loadUsers();
    }

    return () => {
      mounted = false;
    };
  }, [internalNoteMode, mentionableUsers.length]);

  const resolveMentionedUserIds = note => {
    const normalizedNote = normalizeMentionText(note);

    if (!normalizedNote) {
      return [];
    }

    if (/(^|\s)@todos(\s|$)/i.test(String(note || ""))) {
      return mentionableUsers.map(mentionedUser => mentionedUser.id);
    }

    return mentionableUsers
      .filter(mentionedUser => {
        const name = normalizeMentionText(mentionedUser.name);
        const nameParts = String(mentionedUser.name || "")
          .split(/\s+/)
          .map(normalizeMentionText)
          .filter(part => part.length >= 3);
        const emailPrefix = normalizeMentionText(
          String(mentionedUser.email || "").split("@")[0]
        );

        return (
          (name && normalizedNote.includes(name)) ||
          nameParts.some(part => normalizedNote.includes(part)) ||
          (emailPrefix && normalizedNote.includes(emailPrefix))
        );
      })
      .map(mentionedUser => mentionedUser.id);
  };

  const handleToggleInternalNoteMode = () => {
    setInternalNoteMode(prevState => !prevState);
    setShowEmoji(false);
    setTypeBar(false);
    setMentionQuery("");
    setMentionStartIndex(null);
    setMentionHighlightedIndex(0);
  };

  const sendTextMessage = async (messagePayload, templateOverride) => {
    const payload = templateOverride
      ? { ...messagePayload, templateOverride }
      : messagePayload;

    await api.post(`/messages/${ticketId}`, payload);
  };

  const loadTemplateOptions = async () => {
    const connectionId = await resolveOfficialConnectionId(ticket);

    if (!connectionId) {
      throw new Error("Conexao oficial nao encontrada para este ticket.");
    }

    const { data } = await api.get(`/api/whatsapp-official/${connectionId}/templates`, {
      params: {
        status: "APPROVED",
        t: Date.now(),
      },
    });

    const templates = Array.isArray(data?.templates)
      ? data.templates
      : Array.isArray(data)
        ? data
        : [];

    const unique = new Map();

    templates.forEach(template => {
      const name = String(template?.name || "").trim();
      const languageCode = normalizeTemplateLanguageCode(template?.language || "pt_BR");
      const friendlyName = String(template?.friendlyName || "").trim();

      if (!name) {
        return;
      }

      const key = `${name}:${languageCode}`;
      if (!unique.has(key)) {
        unique.set(key, {
          key,
          name,
          languageCode,
          preview: getTemplateBodyPreview(template),
          ...getTemplateVariableInfo(template),
          label: friendlyName
            ? `${friendlyName} - ${name} (${languageCode})`
            : `${name} (${languageCode})`,
        });
      }
    });

    if (!templates.length) {
      return {
        connectionId,
        options: [],
      };
    }

    return {
      connectionId,
      options: Array.from(unique.values()),
    };
  };

  const openTemplateDialog = async messagePayload => {
    setTemplateLoading(true);

    try {
      const { options, connectionId } = await loadTemplateOptions();

      if (!options.length) {
        throw new Error(
          `Nenhum template aprovado foi encontrado para este atendimento (conexao oficial ${connectionId}).`
        );
      }

      const defaultOption = options[0];

      setPendingMessage(messagePayload);
      setTemplateOptions(options);
      setSelectedTemplateKey(defaultOption?.key || "");
      setTemplateParameterValues(
        (defaultOption?.variables || []).reduce((values, variable) => {
          values[variable] = /^(name|nome|1)$/i.test(variable)
            ? String(ticket?.contact?.name || "Cliente")
            : "";
          return values;
        }, {})
      );
      setTemplateDialogOpen(true);
    } catch (err) {
      toastError(err);
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setTemplateOptions([]);
    setSelectedTemplateKey("");
    setTemplateParameterValues({});
    setPendingMessage(null);
  };

  const handleSendSelectedTemplate = async () => {
    const selectedTemplate = templateOptions.find(option => option.key === selectedTemplateKey);

    if (!selectedTemplate || !pendingMessage) {
      return;
    }

    const missingVariables = (selectedTemplate.variables || []).filter(
      variable => !String(templateParameterValues?.[variable] || "").trim()
    );
    if (missingVariables.length) {
      toast.error(i18n.t("messagesInput.templateChooser.parametersRequired"));
      return;
    }

    setTemplateLoading(true);

    try {
      const templateParameters =
        selectedTemplate.format === "NAMED"
          ? {
              namedParameters: (selectedTemplate.variables || []).reduce((values, variable) => {
                values[variable] = String(templateParameterValues[variable] || "").trim();
                return values;
              }, {}),
            }
          : {
              parameters: (selectedTemplate.variables || []).map(variable =>
                String(templateParameterValues[variable] || "").trim()
              ),
            };

      await sendTextMessage(pendingMessage, {
        name: selectedTemplate.name,
        languageCode: selectedTemplate.languageCode,
        ...templateParameters,
      });

      setInputMessage("");
      setShowEmoji(false);
      setReplyingMessage(null);
      handleCloseTemplateDialog();
    } catch (err) {
      toastError(err);
    } finally {
      setTemplateLoading(false);
      setLoading(false);
    }
  };

  const handleChangeInput = e => {
    const value = e.target.value;
    setInputMessage(value);
    if (!internalNoteMode) {
      handleLoadQuickAnswer(value);
    } else {
      setTypeBar(false);
      const mentionToken = getMentionToken(value, e.target.selectionStart || value.length);
      if (mentionToken) {
        setMentionQuery(mentionToken.query);
        setMentionStartIndex(mentionToken.start);
        setMentionHighlightedIndex(0);
      } else {
        setMentionQuery("");
        setMentionStartIndex(null);
        setMentionHighlightedIndex(0);
      }
    }
  };

  const filteredMentionUsers = internalNoteMode && mentionStartIndex !== null
    ? mentionableUsers
        .filter(mentionableUser => {
          const query = normalizeMentionText(mentionQuery);
          if (!query) {
            return true;
          }

          const name = normalizeMentionText(mentionableUser.name);
          const emailPrefix = normalizeMentionText(
            String(mentionableUser.email || "").split("@")[0]
          );

          return name.startsWith(query) || emailPrefix.startsWith(query);
        })
        .slice(0, 8)
    : [];

  const mentionSuggestions = internalNoteMode && mentionStartIndex !== null
    ? [
        {
          id: "__all__",
          type: "all",
          name: i18n.t("messagesInput.internalNote.mentionAll"),
          hint: i18n.t("messagesInput.internalNote.mentionAllHint"),
        },
        ...filteredMentionUsers.map(mentionableUser => ({
          ...mentionableUser,
          type: "user",
          hint: mentionableUser.email || i18n.t("messagesInput.internalNote.mentionUserHint"),
        })),
      ]
    : [];

  const closeMentionSuggestions = () => {
    setMentionQuery("");
    setMentionStartIndex(null);
    setMentionHighlightedIndex(0);
  };

  const handleMentionSelect = suggestion => {
    if (mentionStartIndex === null) {
      return;
    }

    const tokenText =
      suggestion.type === "all" ? "Todos" : getMentionTokenText(suggestion);
    const beforeMention = inputMessage.slice(0, mentionStartIndex);
    const cursorPosition = inputRef.current?.selectionStart || inputMessage.length;
    const afterMention = inputMessage.slice(cursorPosition);
    const nextValue = `${beforeMention}@${tokenText} ${afterMention.replace(/^\s+/, "")}`;
    const nextCursorPosition = `${beforeMention}@${tokenText} `.length;

    setInputMessage(nextValue);
    closeMentionSuggestions();

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(nextCursorPosition, nextCursorPosition);
      }
    }, 0);
  };

  const handleInputKeyDown = e => {
    if (mentionSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionHighlightedIndex(prevIndex =>
          prevIndex >= mentionSuggestions.length - 1 ? 0 : prevIndex + 1
        );
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionHighlightedIndex(prevIndex =>
          prevIndex <= 0 ? mentionSuggestions.length - 1 : prevIndex - 1
        );
        return;
      }

      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        handleMentionSelect(mentionSuggestions[mentionHighlightedIndex]);
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        closeMentionSuggestions();
      }
    }
  };

  const handleQuickAnswersClick = value => {
    setInputMessage(value);
    setTypeBar(false);
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (internalNoteMode) {
      return;
    }

    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = e => {
    if (internalNoteMode) {
      return;
    }

    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async e => {
    if (internalNoteMode) {
      return;
    }

    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach(media => {
      formData.append("medias", media);
      formData.append("body", media.name);
    });

    try {
      await api.post(`/messages/${ticketId}`, formData, {
        timeout: 120000,
      });
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);
    let sentSuccessfully = false;
    const shouldSignMessage = allowSignMessageChoice ? signMessage : true;

    if (internalNoteMode) {
      try {
        await api.post(`/messages/${ticketId}/internal-notes`, {
          note: inputMessage.trim(),
          mentionedUserIds: resolveMentionedUserIds(inputMessage),
        });
        sentSuccessfully = true;
      } catch (err) {
        toastError(err);
      }

      if (sentSuccessfully) {
        setInputMessage("");
        setShowEmoji(false);
        setReplyingMessage(null);
        closeMentionSuggestions();
        onInternalNoteSent();
      }

      setLoading(false);
      return;
    }

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: shouldSignMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    try {
      await sendTextMessage(message);
      sentSuccessfully = true;
    } catch (err) {
      const errorKey = getErrorKey(err);
      if (OUTSIDE_24H_ERROR_KEYS.has(errorKey)) {
        await openTemplateDialog(message);
      } else {
        toastError(err);
      }
    }

    if (sentSuccessfully) {
      setInputMessage("");
      setShowEmoji(false);
      setReplyingMessage(null);
    }

    setLoading(false);
  };

  const renderMentionSuggestions = () => {
    if (!internalNoteMode || mentionSuggestions.length === 0) {
      return null;
    }

    return (
      <ul className={classes.mentionSuggestionsWrapper}>
        {mentionSuggestions.map((suggestion, index) => (
          <li
            className={clsx(classes.mentionSuggestionItem, {
              [classes.mentionSuggestionItemActive]: index === mentionHighlightedIndex,
            })}
            key={suggestion.id}
            onMouseDown={event => {
              event.preventDefault();
              handleMentionSelect(suggestion);
            }}
          >
            <Avatar
              className={clsx(classes.mentionSuggestionAvatar, {
                [classes.mentionSuggestionAllAvatar]: suggestion.type === "all",
              })}
              src={suggestion.type === "user" ? suggestion.profilePicUrl : undefined}
            >
              {suggestion.type === "all"
                ? "@"
                : getMentionDisplayName(suggestion).slice(0, 1).toUpperCase()}
            </Avatar>
            <span className={classes.mentionSuggestionText}>
              <span className={classes.mentionSuggestionName}>
                {suggestion.name}
              </span>
              <span className={classes.mentionSuggestionHint}>
                {suggestion.hint}
              </span>
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const handleStartRecording = async () => {
    if (internalNoteMode) {
      return;
    }

    setLoading(true);
    try {
      if (!audioRecorderRef.current) {
        audioRecorderRef.current = new AudioRecorderService();
      }

      await audioRecorderRef.current.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(new Error(i18n.t(getAudioRecorderErrorKey(err))));
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async value => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: inputMessage.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      if (!audioRecorderRef.current) {
        const error = new Error("No audio recording is active");
        error.name = "EmptyAudioError";
        throw error;
      }

      const blob = await audioRecorderRef.current.stop();

      const formData = new FormData();
      const filename = `${new Date().getTime()}.mp3`;
      const audioFile = new File([blob], filename, { type: "audio/mpeg" });
      formData.append("medias", audioFile, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData, {
        timeout: 120000,
      });
    } catch (err) {
      if (err?.response || err?.code === "ERR_NETWORK") {
        toastError(err);
      } else {
        toastError(new Error(i18n.t(getAudioRecorderErrorKey(err))));
      }
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      if (audioRecorderRef.current) {
        await audioRecorderRef.current.cancel();
      }
      setRecording(false);
    } catch (err) {
      toastError(new Error(i18n.t(getAudioRecorderErrorKey(err))));
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = () => {
    setAnchorEl(null);
  };

  const renderReplyingMessage = message => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  const renderTemplateDialog = () => {
    const selectedTemplate = templateOptions.find(option => option.key === selectedTemplateKey);
    const previewText = fillTemplatePreviewWithValues(
      selectedTemplate?.preview,
      templateParameterValues,
      ticket?.contact?.name
    );

    return (
      <Dialog
        open={templateDialogOpen}
        onClose={templateLoading ? undefined : handleCloseTemplateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {i18n.t("messagesInput.templateChooser.title")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("messagesInput.templateChooser.description")}
          </Typography>
          <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel id="outside24h-template-select-label">
              {i18n.t("messagesInput.templateChooser.selectLabel")}
            </InputLabel>
            <Select
              labelId="outside24h-template-select-label"
              value={selectedTemplateKey}
              onChange={e => {
                const nextKey = e.target.value;
                const nextTemplate = templateOptions.find(option => option.key === nextKey);
                setSelectedTemplateKey(nextKey);
                setTemplateParameterValues(
                  (nextTemplate?.variables || []).reduce((values, variable) => {
                    values[variable] = /^(name|nome|1)$/i.test(variable)
                      ? String(ticket?.contact?.name || "Cliente")
                      : "";
                    return values;
                  }, {})
                );
              }}
              label={i18n.t("messagesInput.templateChooser.selectLabel")}
              disabled={templateLoading}
            >
              {templateOptions.map(option => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(selectedTemplate?.variables || []).map(variable => (
            <TextField
              key={variable}
              fullWidth
              required
              margin="normal"
              variant="outlined"
              label={i18n.t("messagesInput.templateChooser.parameterLabel", { variable })}
              value={templateParameterValues?.[variable] || ""}
              onChange={event => {
                const value = event.target.value;
                setTemplateParameterValues(previous => ({
                  ...previous,
                  [variable]: value,
                }));
              }}
            />
          ))}
          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            multiline
            rows={4}
            label={i18n.t("messagesInput.templateChooser.previewLabel")}
            value={previewText}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              readOnly: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseTemplateDialog}
            disabled={templateLoading}
            className={classes.modalActionButton}
          >
            {i18n.t("messagesInput.templateChooser.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disableElevation
            className={classes.modalActionButton}
            onClick={handleSendSelectedTemplate}
            disabled={templateLoading || !selectedTemplateKey}
          >
            {templateLoading
              ? i18n.t("messagesInput.templateChooser.sending")
              : i18n.t("messagesInput.templateChooser.send")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (medias.length > 0)
    return (
      <>
        <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
          <IconButton
            aria-label="cancel-upload"
            component="span"
            onClick={() => setMedias([])}
          >
            <CancelIcon className={classes.sendMessageIcons} />
          </IconButton>

          {loading ? (
            <div>
              <CircularProgress className={classes.circleLoading} />
            </div>
          ) : (
            <span>
              {medias[0]?.name}
              {/* <img src={media.preview} alt=""></img> */}
            </span>
          )}
          <IconButton
            aria-label="send-upload"
            component="span"
            onClick={handleUploadMedia}
            disabled={loading}
          >
            <SendIcon className={classes.sendMessageIcons} />
          </IconButton>
        </Paper>
        {renderTemplateDialog()}
      </>
    );
  else {
    return (
      <>
        <Paper square elevation={0} className={classes.mainWrapper}>
          {internalNoteMode && (
            <div className={classes.internalModeHint}>
              {i18n.t("messagesInput.internalNote.activeHint")}
            </div>
          )}
          {replyingMessage && renderReplyingMessage(replyingMessage)}
          <div className={classes.newMessageBox}>
          <Hidden only={["sm", "xs"]}>
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={loading || recording || ticketStatus !== "open" || internalNoteMode}
              onClick={() => setShowEmoji(prevState => !prevState)}
            >
              <MoodIcon className={classes.sendMessageIcons} />
            </IconButton>
            <IconButton
              aria-label={i18n.t("messagesInput.internalNote.toggle")}
              component="span"
              disabled={loading || recording || ticketStatus !== "open"}
              onClick={handleToggleInternalNoteMode}
              title={i18n.t("messagesInput.internalNote.toggle")}
            >
              <ChatBubbleOutlineIcon
                className={clsx(classes.sendMessageIcons, {
                  [classes.internalModeIconActive]: internalNoteMode,
                })}
              />
            </IconButton>
            {showEmoji ? (
              <div className={classes.emojiBox}>
                <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                  <Picker
                    perLine={16}
                    showPreview={false}
                    showSkinTones={false}
                    onSelect={handleAddEmoji}
                  />
                </ClickAwayListener>
              </div>
            ) : null}

            <input
              multiple
              type="file"
              id="upload-button"
              disabled={loading || recording || ticketStatus !== "open" || internalNoteMode}
              className={classes.uploadInput}
              onChange={handleChangeMedias}
            />
            <label htmlFor="upload-button">
              <IconButton
                aria-label="upload"
                component="span"
                disabled={loading || recording || ticketStatus !== "open" || internalNoteMode}
              >
                <AttachFileIcon className={classes.sendMessageIcons} />
              </IconButton>
            </label>
            {allowSignMessageChoice && (
              <FormControlLabel
                style={{ marginRight: 7, color: "gray" }}
                label={i18n.t("messagesInput.signMessage")}
                labelPlacement="start"
                control={
                  <Switch
                    size="small"
                    checked={signMessage}
                    onChange={e => {
                      setSignMessage(e.target.checked);
                    }}
                    name="showAllTickets"
                    color="primary"
                  />
                }
              />
            )}
          </Hidden>
          <Hidden only={["md", "lg", "xl"]}>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleOpenMenuClick}
            >
              <MoreVert></MoreVert>
            </IconButton>
            <Menu
              id="simple-menu"
              keepMounted
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuItemClick}
            >
              <MenuItem onClick={handleMenuItemClick}>
                <IconButton
                  aria-label="emojiPicker"
                  component="span"
                  disabled={loading || recording || ticketStatus !== "open" || internalNoteMode}
                  onClick={() => setShowEmoji(prevState => !prevState)}
                >
                  <MoodIcon className={classes.sendMessageIcons} />
                </IconButton>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <input
                  multiple
                  type="file"
                  id="upload-button"
                  disabled={loading || recording || ticketStatus !== "open" || internalNoteMode}
                  className={classes.uploadInput}
                  onChange={handleChangeMedias}
                />
                <label htmlFor="upload-button">
                  <IconButton
                    aria-label="upload"
                    component="span"
                    disabled={loading || recording || ticketStatus !== "open" || internalNoteMode}
                  >
                    <AttachFileIcon className={classes.sendMessageIcons} />
                  </IconButton>
                </label>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <IconButton
                  aria-label={i18n.t("messagesInput.internalNote.toggle")}
                  component="span"
                  disabled={loading || recording || ticketStatus !== "open"}
                  onClick={handleToggleInternalNoteMode}
                >
                  <ChatBubbleOutlineIcon
                    className={clsx(classes.sendMessageIcons, {
                      [classes.internalModeIconActive]: internalNoteMode,
                    })}
                  />
                </IconButton>
              </MenuItem>
              {allowSignMessageChoice && (
                <MenuItem onClick={handleMenuItemClick}>
                  <FormControlLabel
                    style={{ marginRight: 7, color: "gray" }}
                    label={i18n.t("messagesInput.signMessage")}
                    labelPlacement="start"
                    control={
                      <Switch
                        size="small"
                        checked={signMessage}
                        onChange={e => {
                          setSignMessage(e.target.checked);
                        }}
                        name="showAllTickets"
                        color="primary"
                      />
                    }
                  />
                </MenuItem>
              )}
            </Menu>
          </Hidden>
          <div
            className={clsx(classes.messageInputWrapper, {
              [classes.internalMessageInputWrapper]: internalNoteMode,
            })}
          >
            <InputBase
              inputRef={input => {
                input && input.focus();
                input && (inputRef.current = input);
              }}
              className={classes.messageInput}
              placeholder={
                ticketStatus === "open"
                  ? internalNoteMode
                    ? i18n.t("messagesInput.internalNote.placeholder")
                    : i18n.t("messagesInput.placeholderOpen")
                  : i18n.t("messagesInput.placeholderClosed")
              }
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={recording || loading || ticketStatus !== "open"}
              onKeyDown={handleInputKeyDown}
              onPaste={e => {
                ticketStatus === "open" && handleInputPaste(e);
              }}
              onKeyPress={e => {
                if (loading || e.shiftKey) return;
                else if (e.key === "Enter") {
                  handleSendMessage();
                }
              }}
            />
            {typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {quickAnswers.map((value, index) => {
                  return (
                    <li
                      className={classes.messageQuickAnswersWrapperItem}
                      key={index}
                    >
                      <a onClick={() => handleQuickAnswersClick(value.message)}>
                        {`${value.shortcut} - ${value.message}`}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : (
              renderMentionSuggestions()
            )}
          </div>
          {inputMessage ? (
            <IconButton
              aria-label="sendMessage"
              component="span"
              onClick={handleSendMessage}
              disabled={loading}
            >
              {internalNoteMode ? (
                <ChatBubbleOutlineIcon className={classes.internalModeIconActive} />
              ) : (
                <SendIcon className={classes.sendMessageIcons} />
              )}
            </IconButton>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
            <IconButton
              aria-label="showRecorder"
              component="span"
              disabled={loading || ticketStatus !== "open" || internalNoteMode}
              onClick={handleStartRecording}
            >
              <MicIcon className={classes.sendMessageIcons} />
            </IconButton>
          )}
          </div>
        </Paper>
        {renderTemplateDialog()}
      </>
    );
  }
};

export default MessageInput;
